import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const messages = [
  {
    key: "p1",
    number: "01",
    imageUrl: "https://readdy.ai/api/search-image?query=A%20professional%20team%20of%20Japanese%20business%20specialists%20in%20a%20modern%20Tokyo%20office%2C%20reviewing%20documents%20and%20strategy%20plans%20together%2C%20clean%20minimalist%20workspace%20with%20floor-to-ceiling%20windows%20overlooking%20the%20city%20skyline%2C%20warm%20natural%20light%2C%20sophisticated%20corporate%20atmosphere%2C%20muted%20tones%20of%20white%20and%20charcoal%2C%20editorial%20photography%20style&width=900&height=600&seq=about-msg-01&orientation=landscape",
    accent: "BRIDGE",
  },
  {
    key: "p2",
    number: "02",
    imageUrl: "https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20global%20trade%20and%20Japan%20market%20entry%2C%20world%20map%20with%20glowing%20connection%20lines%20between%20countries%20and%20Japan%2C%20dark%20navy%20background%20with%20subtle%20gold%20accents%2C%20sophisticated%20data%20visualization%20aesthetic%2C%20premium%20business%20concept%2C%20clean%20and%20modern%20graphic%20style&width=900&height=600&seq=about-msg-02&orientation=landscape",
    accent: "SUPPORT",
  },
  {
    key: "p3",
    number: "03",
    imageUrl: "https://readdy.ai/api/search-image?query=Small%20dedicated%20team%20of%20professionals%20in%20a%20bright%20modern%20meeting%20room%2C%20having%20a%20focused%20one-on-one%20consultation%2C%20warm%20collaborative%20atmosphere%2C%20minimalist%20Scandinavian%20office%20interior%2C%20natural%20wood%20tones%20and%20white%20walls%2C%20genuine%20human%20connection%20and%20trust%2C%20candid%20documentary%20photography%20style&width=900&height=600&seq=about-msg-03&orientation=landscape",
    accent: "PARTNER",
  },
];

export default function AboutSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.08 }
    );
    const children = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    children?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 md:py-40 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Top label */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 mb-16 md:mb-24">
          <div className="flex items-center gap-4">
            <div className="w-8 h-px bg-[#1a2332]"></div>
            <p className="text-xs text-gray-400 tracking-[0.4em] uppercase">
              {t("about.label")} / {t("about.labelJa")}
            </p>
          </div>
        </div>

        {/* Large heading */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100 mb-20 md:mb-28">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-[#1a2332] leading-[1.05] tracking-tight">
            {t("about.title")}
          </h2>
        </div>

        {/* Message panels */}
        <div className="space-y-0">
          {messages.map((msg, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <div
                key={msg.key}
                className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
                style={{ transitionDelay: `${idx * 120}ms` }}
              >
                <div className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} min-h-[480px] md:min-h-[560px]`}>

                  {/* Image side */}
                  <div className="w-full lg:w-[55%] relative overflow-hidden">
                    <div className="w-full h-[300px] md:h-[400px] lg:h-full min-h-[400px]">
                      <img
                        src={msg.imageUrl}
                        alt={msg.accent}
                        className="w-full h-full object-cover object-top"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-[#1a2332]/20"></div>
                      {/* Number watermark */}
                      <div className="absolute bottom-6 right-8 text-white/10 text-[100px] md:text-[140px] font-black leading-none select-none">
                        {msg.number}
                      </div>
                      {/* Accent word */}
                      <div className="absolute top-8 left-8">
                        <span className="text-xs text-white/60 tracking-[0.4em] uppercase font-medium">
                          {msg.accent}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Text side */}
                  <div className={`w-full lg:w-[45%] bg-[#f8f7f4] flex flex-col justify-center px-10 md:px-14 lg:px-16 py-14 md:py-16 relative`}>
                    {/* Large number accent */}
                    <div className="absolute top-8 right-10 text-[#1a2332]/5 text-8xl font-black leading-none select-none">
                      {msg.number}
                    </div>

                    {/* Divider line */}
                    <div className="w-10 h-0.5 bg-[#1a2332] mb-8"></div>

                    {/* Message text */}
                    <p className="text-gray-700 text-lg md:text-xl leading-[1.9] font-light relative z-10">
                      {t(`about.${msg.key}`)}
                    </p>
                  </div>
                </div>

                {/* Thin divider between panels */}
                {idx < messages.length - 1 && (
                  <div className="w-full h-px bg-gray-100"></div>
                )}
              </div>
            );
          })}
        </div>



      </div>
    </section>
  );
}
